import { useParams } from 'react-router-dom'
import {
  MissingInfoPopupType,
  OverallMiStatusPropType,
  ViewInputType,
} from '../../types/MissingInformationSummary.types'
import {
  Accordion,
  CustomButton,
  SvgQueueEdit,
  SvgTrash,
  TableColumnType,
  Tooltip,
} from '@ui-kit'
import { DataType, DataTypeStore, EVENT_CONSTANTS } from '@app-store'
import { MISSING_INFO_POPUP_CONSTANTS, NAMESPACES } from '../../utils/constants'
import { useEffect, useState } from 'react'
import { TAB_INDEX } from '../../constants/form.constants'
import PersistTable from '../../common/PersistTable'
import { formatLocalDateWithZone } from '../../utils/date.utils'
import AddMissingInformation from './AddMissingInformation'
import { isReadOnlyUser } from '../../utils/permission'
import { useAppSelector } from '../../utils/agent.store'

export const MissingInfoPopup = ({
  pendingMiData,
  setPendingMiData,
  overallMiStatus,
  resolvedMiData,
  handleReValidate,
  handleSave,
  handleCancel,
  originalMissingData,
  miFields,
  caseStatusVoidedOrClosed,
}: MissingInfoPopupType) => {
  const { caseId } = useParams()
  const workflowInstance = useAppSelector(
    (state) => state?.workflowData?.workflowInstance
  )
  const [pendingTableHeight, setPendingTableHeight] = useState<number>(10)
  const [resolvedMiDataHeight, setResolvedMiDataHeight] = useState<number>(10)

  const columnList: TableColumnType<DataTypeStore>[] = [
    {
      header: MISSING_INFO_POPUP_CONSTANTS.MI_FIELD,
      accessor: MISSING_INFO_POPUP_CONSTANTS.MI_FIELD_LOWERCASE,
      isSortable: false,
    },
    {
      header: MISSING_INFO_POPUP_CONSTANTS.MI_FIELD_TYPE_LABEL,
      accessor: MISSING_INFO_POPUP_CONSTANTS.MI_FIELD_TYPE,
      isSortable: false,
    },
    {
      header: NAMESPACES.DESCRIPTION_LABEL,
      accessor: NAMESPACES.MESSAGE_KEY,
      isSortable: false,
    },
    {
      header: MISSING_INFO_POPUP_CONSTANTS.MI_START_DATE,
      accessor: MISSING_INFO_POPUP_CONSTANTS.MI_START_DATE_LOWERCASE,
      isSortable: false,
    },
    {
      header: NAMESPACES.STATUS,
      accessor: NAMESPACES.STATUS_KEY,
      isSortable: false,
      type: MISSING_INFO_POPUP_CONSTANTS.COMPONENT,
      renderComponent: (row: DataType | null) => {
        return (
          <div className="flex items-center gap-1">
            <div className="text-xs font-light">{row?.status}</div>
            {row?.changed && (
              <div className="w-[3.8rem] h-[1.2rem] rounded-sm bg-yellow-100 flex items-center justify-center p-2">
                <span className="text-xs font-light text-black">
                  {MISSING_INFO_POPUP_CONSTANTS.UNSAVED}
                </span>
              </div>
            )}
          </div>
        )
      },
    },
  ]
  const resolvedList: TableColumnType<DataTypeStore>[] = [
    ...columnList,
    {
      header: MISSING_INFO_POPUP_CONSTANTS.MI_END_DATE,
      accessor: MISSING_INFO_POPUP_CONSTANTS.MI_END_DATE_LOWERCASE,
      isSortable: false,
    },
  ]
  const actions = [
    {
      label: NAMESPACES.EDIT_BUTTON,
      key: NAMESPACES.EDIT_KEY,
      accessor: NAMESPACES.EDIT_KEY,
      showInPopUp: false,
      type: MISSING_INFO_POPUP_CONSTANTS.COMPONENT,
      isEnabledOnDisabledRow: false,
      renderActionComponent: (row: DataType | null, rowIndex: number) => {
        if (row?.manually_inserted) {
          return (
            <Tooltip content={NAMESPACES.EDIT_BUTTON} position="top">
              <button
                type="button"
                onClick={() => {
                  setSelectedRow({ ...row, rowIndex: rowIndex })
                  setPendingMiData((prev) =>
                    prev.map((item, i) =>
                      i === rowIndex
                        ? { ...item, disabled: true }
                        : { ...item, disabled: false }
                    )
                  )
                  window.dispatchEvent(
                    new Event(EVENT_CONSTANTS.DISABLE_FOCUS_TRAP)
                  )
                }}
                className={`${row?.disabled ? 'pointer-events-none' : ''}`}
                data-qa-id="edit-mi-info"
              >
                <SvgQueueEdit />
              </button>
            </Tooltip>
          )
        } else return null
      },
    },
    {
      label: NAMESPACES.DELETE,
      key: NAMESPACES.DELETE_KEY,
      accessor: NAMESPACES.DELETE_KEY,
      showInPopUp: false,
      type: MISSING_INFO_POPUP_CONSTANTS.COMPONENT,
      isEnabledOnDisabledRow: false,
      renderActionComponent: (row: DataType | null, rowIndex: number) => {
        if (row?.manually_inserted && !row.id) {
          return (
            <Tooltip content={NAMESPACES.DELETE} position="top">
              <button
                type="button"
                onClick={() => {
                  const updatedMiFields = pendingMiData.filter(
                    (_, i) => i !== rowIndex
                  )
                  setPendingMiData(updatedMiFields)
                  addPendingMiDataInSession(updatedMiFields)
                }}
                className={`${row?.disabled ? 'pointer-events-none' : ''}`}
                data-qa-id="delete-mi-info"
              >
                <SvgTrash />
              </button>
            </Tooltip>
          )
        } else return null
      },
    },
  ]
  const [selectedRow, setSelectedRow] = useState<DataType | null>(null)

  useEffect(() => {
    setPendingTableHeight(pendingMiData?.length >= 3 ? 15 : 10)
  }, [pendingMiData])

  useEffect(() => {
    setResolvedMiDataHeight(resolvedMiData?.length >= 3 ? 15 : 10)
  }, [resolvedMiData])

  const hasObjectChanged = (
    original: DataType | undefined,
    updated: DataType | undefined
  ): boolean => {
    if (!original && updated) return true
    if (!updated && original) return true
    if (!original && !updated) return false

    return Object.keys({ ...original }).some((key) => {
      const origValue = original?.[key]
      const updatedValue = updated?.[key]
      if (typeof origValue === 'object' && typeof updatedValue === 'object') {
        return JSON.stringify(origValue) !== JSON.stringify(updatedValue)
      }

      return origValue !== updatedValue
    })
  }
  const addPendingMiDataInSession = (data: DataType[]) => {
    const stored = sessionStorage.getItem(
      MISSING_INFO_POPUP_CONSTANTS.PENDING_MI_DATA
    )
    let parsed
    if (!stored) {
      parsed = {
        openModal: false,
        caseId: caseId,
        manual_mi_fields: [],
      }
    } else {
      parsed = JSON.parse(stored)
    }
    parsed = { ...parsed, manual_mi_fields: data }

    sessionStorage.setItem(
      MISSING_INFO_POPUP_CONSTANTS.PENDING_MI_DATA,
      JSON.stringify(parsed)
    )
  }

  const findByFieldLabel = (label: string) =>
    miFields.find((item) => item.mi_field === label)
  const onSubmit = (data: DataType | null) => {
    if (data && selectedRow) {
      const updatedMiData = pendingMiData.map((item, i) => {
        let updatedRowData: DataType | null = null
        if (i !== selectedRow?.rowIndex) return item

        const originalItem = originalMissingData.find(
          (orig) => orig.id === item.id || orig.mi_field === item.mi_field
        )

        const mergedData = { ...item, ...data }
        const isChanged = hasObjectChanged(originalItem, mergedData)

        updatedRowData = {
          ...mergedData,
          changed: isChanged,
          disabled: false,
        }

        return updatedRowData
      })
      setPendingMiData(updatedMiData)

      addPendingMiDataInSession(updatedMiData)
    } else if (data) {
      const selectedMiField = findByFieldLabel(data?.mi_field as string)

      const newItem = {
        ...data,
        case_id: caseId ?? '',
        manually_inserted: true,
        mi_code: selectedMiField?.mi_code as string,
        template_label: selectedMiField?.template_label as string,
        description: selectedMiField?.description as string,
        mi_field_type: MISSING_INFO_POPUP_CONSTANTS.MANUAL,
        changed: true,
        stepper: workflowInstance?.current_group as string,
        manual_mi_id: selectedMiField?.manual_mi_id as string,
        additional_fields: selectedMiField?.additional_fields as string,
      }

      setPendingMiData((prev) => [newItem, ...prev])
      addPendingMiDataInSession([newItem, ...pendingMiData])
    } else {
      setPendingMiData((prev) =>
        prev.map((item) => ({
          ...item,
          disabled: false,
        }))
      )
    }

    setSelectedRow(null)
  }
  return (
    <div className="flex flex-col gap-1 pb-[0.625rem]">
      <span className="flex justify-between items-center mb-1">
        <span className="text-xl font-medium">
          {MISSING_INFO_POPUP_CONSTANTS.MISSING_INFORMATION_SUMMARY}
        </span>
        <span className="flex gap-3">
          <CustomButton type="button" onClick={handleCancel}>
            {NAMESPACES.CANCEL_BUTTON}
          </CustomButton>
          {!isReadOnlyUser() && !caseStatusVoidedOrClosed && (
            <>
              <CustomButton isFilled type="button" onClick={handleReValidate}>
                {MISSING_INFO_POPUP_CONSTANTS.RE_VALIDATE}
              </CustomButton>
              <CustomButton isFilled type="button" onClick={handleSave}>
                {NAMESPACES.SAVE_BUTTON}
              </CustomButton>
            </>
          )}
        </span>
      </span>

      <OverCaseDetails overallMiStatus={overallMiStatus} />
      {!isReadOnlyUser() && !caseStatusVoidedOrClosed && (
        <AddMissingInformation
          onSubmit={onSubmit}
          selectedRow={selectedRow}
          miFields={miFields}
        />
      )}

      <Accordion
        heading={MISSING_INFO_POPUP_CONSTANTS.PENDING_MI_FIELDS}
        tabIndex={TAB_INDEX}
        className="bg-none p-[0.25rem]"
        borderColor="secondary-500"
        isDisabled={false}
        isDefaultActive={true}
      >
        <div className="table-wrap flex w-full">
          <PersistTable
            tableName="pending-mi-fields"
            data={pendingMiData}
            columns={columnList}
            actions={
              !isReadOnlyUser() && !caseStatusVoidedOrClosed
                ? actions
                : undefined
            }
            disabledKey="disabled"
            emptyDataMessage="No record found."
            minHeight={`${pendingTableHeight}rem`}
            isPersistColumns={false}
            emptyDataMessageHeight="min-h-32 max-h-32"
          />
        </div>
      </Accordion>
      <Accordion
        heading={MISSING_INFO_POPUP_CONSTANTS.RESOLVED_MI_FIELDS}
        tabIndex={TAB_INDEX}
        className="bg-none"
        borderColor="secondary-500"
        isDisabled={false}
        isDefaultActive={true}
      >
        <div className="table-wrap flex w-full">
          <PersistTable
            tableName="resolved-mi-fields"
            data={resolvedMiData}
            columns={resolvedList}
            disabledKey="disabled"
            emptyDataMessage="No record found."
            isPersistColumns={false}
            minHeight={`${resolvedMiDataHeight}rem`}
            emptyDataMessageHeight="min-h-32 max-h-32"
          />
        </div>
      </Accordion>
    </div>
  )
}

export const OverCaseDetails = ({
  overallMiStatus,
}: OverallMiStatusPropType) => {
  return (
    <Accordion
      heading={MISSING_INFO_POPUP_CONSTANTS.OVERALL_MI_CASE_DETAILS}
      tabIndex={TAB_INDEX}
      className="bg-none"
      borderColor="secondary-500"
      isDisabled={false}
      isDefaultActive={true}
    >
      <div className="flex gap-[3.28125rem] justify-between w-full p-[0.75rem]">
        <ViewInput
          label={MISSING_INFO_POPUP_CONSTANTS.MI_START_DATE}
          value={
            formatLocalDateWithZone(overallMiStatus?.mi_start_date as string) ??
            ''
          }
        />
        <ViewInput
          label={MISSING_INFO_POPUP_CONSTANTS.MI_END_DATE}
          value={
            formatLocalDateWithZone(overallMiStatus?.mi_end_date as string) ??
            ''
          }
        />
        <ViewInput
          label={MISSING_INFO_POPUP_CONSTANTS.MI_TIME_TOTAL}
          value={(overallMiStatus?.mi_duration as string) ?? ''}
        />
      </div>
    </Accordion>
  )
}

export const ViewInput = ({ label, value }: ViewInputType) => {
  return (
    <div className="break-words w-1/3 justify-start">
      <Tooltip content={label}>
        <div className="font-extralight break-words text-xs">{label}</div>
      </Tooltip>
      <div className="font-light text-xs">{value}</div>
    </div>
  )
}
